import { useMutation } from "@apollo/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Note3 from "components/Note/Note3";
import { ModernButton } from "components/ui/modernButton";
import {
  ComposeActions,
  ComposeDivider,
  ComposeHelpTrigger,
  ComposeSection,
} from "components/ui/v2";
import { useModal } from "context/modal/modal.provider";
import { gqlMessage } from "gql";
import { ICreateMessageInput } from "gql/Message/mutations";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Popover } from "rsuite";
import TagInput from "shadcn/components/ui/custom/tagInput/tagInput";
import { Separator } from "shadcn/components/ui/separator";
import useCapsuleTagsStore from "stores/capsuleTags/useCapsuleTagsStore";
import { EMessageType, IMessage } from "types/Message.type";
import { IUser } from "types/User.types";
import useSound from "use-sound";
import { checkIfNodeHaveText } from "utils/message";
import { IForm } from "./new-message.types";

interface INewMagazineMessageProps {
  resetMessageInput: () => void;
  usersToMention: IUser[];
  inline?: boolean;
}
export default function NewMessageCapsule(props: INewMagazineMessageProps) {
  const { resetMessageInput, usersToMention, inline } = props;
  const [playComplete] = useSound("/sounds/feedback-sound.mp3", {
    volume: 0.25
  });
  const { closeModal } = useModal();
  const { t, lang } = useTranslation("common");
  const { t: tExplore } = useTranslation("explore");
  const [contentEmpty, setContentEmpty] = useState<boolean>(true);
  const [isDisabledHandler, setIsDisabledHandler] = useState<boolean>(true);
  const { control, handleSubmit, setValue, watch } = useForm<IForm>();
  const [tags, setTags] = useState<string[] | undefined>(undefined);
  const { dataAllCapsuleTags } = useCapsuleTagsStore();

  const [createMessage, { loading: createMessageLoading }] = useMutation<
    { createMessage: IMessage },
    { createMessageInput: ICreateMessageInput }
  >(gqlMessage.mutations.CREATE_MESSAGE, {
    onCompleted: () => {
      setValue("message", "");
      resetMessageInput();
      toast.success(t("Mensaje publicado correctamente"));
      playComplete();
      closeModal();
    },
    refetchQueries: [{ query: gqlMessage.queries.GET_TAGS }],
    awaitRefetchQueries: true
  });

  const handlePublish = (data: IForm) => {
    createMessage({
      variables: {
        createMessageInput: {
          type: EMessageType.CAPSULES,
          text: data?.message,
          tags: tags
        }
      }
    });
  };

  const isValidJSON = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const speaker = (
    <Popover title={t("✨ Copia y pega el embebido de Loom o Youtube")}>
      <div className="flex flex-col items-start">
        <Image
          src={
            lang === "en"
              ? "https://cdn.ndrz.io/groowcity/general/how-to-embed-canva-en.gif"
              : "https://cdn.ndrz.io/groowcity/general/how-to-embed-canva.gif"
          }
          width={480}
          height={480}
          alt="how-to-embed-canvas"
          className="rounded-lg"
        />
      </div>
    </Popover>
  );

  useEffect(() => {
    const value = watch("message");
    if (value && isValidJSON(value)) {
      const blocks = JSON.parse(value);
      const hasText = checkIfNodeHaveText(blocks);
      setContentEmpty(!hasText && tags === undefined);
    } else {
      setContentEmpty(true && tags !== undefined);
    }
  }, [watch("message"), tags]);

  useEffect(() => {
    if (contentEmpty || tags === undefined) {
      setIsDisabledHandler(true);
    }

    if (!contentEmpty && tags !== undefined) {
      setIsDisabledHandler(false);
    }
  }, [tags, contentEmpty]);

  if (inline) {
    return (
      <>
        <ComposeSection compact>
          <Controller
            name="message"
            control={control}
            render={({ field }) => (
              <Note3
                {...field}
                compact
                onChange={(e) => field.onChange(e)}
                placeholder={tExplore("Pega el embed de Canva, Loom o YouTube")}
                usersToMention={usersToMention}
                enableImageButton={false}
                enableTextButtons={true}
                enableLinkButton={true}
              />
            )}
          />
        </ComposeSection>
        <ComposeDivider compact />
        <ComposeSection compact>
          <TagInput
            tags={tags}
            setTags={setTags}
            maxTags={3}
            variant="violet"
            inputSize="sm"
            label={tExplore("Tags")}
            suggestions={dataAllCapsuleTags || []}
          />
        </ComposeSection>
        <ComposeActions
          compact
          onPublish={handleSubmit(handlePublish)}
          loading={createMessageLoading}
          disabled={isDisabledHandler}
          loadingText={t("Publicando...")}
          publishText={t("Publicar")}
          publishVariant="violet"
          secondary={
            <ComposeHelpTrigger
              label={t("¿Como funciona?")}
              speaker={speaker}
              accent="violet"
            />
          }
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Content Creation Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">
            {tExplore("Crea tu contenido")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate__animated animate__fadeIn relative">
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <Note3
                  {...field}
                  onChange={(e) => field.onChange(e)}
                  placeholder={tExplore(
                    "✨ Copia y pega el <embed> de Canva, Loom o Youtube"
                  )}
                  usersToMention={usersToMention}
                  enableImageButton={false}
                  enableTextButtons={true}
                  enableLinkButton={true}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Tags Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">
            {tExplore("Agrega los tags asociados")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <TagInput
            tags={tags}
            setTags={setTags}
            maxTags={3}
            variant="violet"
            inputSize="sm"
            label={tExplore("Tags")}
            suggestions={dataAllCapsuleTags || []}
          />
          <p className="text-xs text-muted-foreground">
            <span className="text-red-500">*</span>{" "}
            {tExplore(
              "Es importante que agregues tags para que tu mensaje sea encontrado"
            )}
          </p>
        </CardContent>
      </Card>

      {/* Action Section */}
      <div className="flex items-center justify-between pt-4">
        <ComposeHelpTrigger
          label={t("¿Como funciona?")}
          speaker={speaker}
          placement="left"
          accent="violet"
        />

        <ModernButton
          variant="primary"
          onClick={handleSubmit(handlePublish)}
          loading={createMessageLoading}
          loadingText={t("Publicando...")}
          disabled={isDisabledHandler}
          className="h-12 min-w-[140px] px-8 py-3 text-base font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
        >
          {createMessageLoading ? (
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              <span>{t("Publicando...")}</span>
            </div>
          ) : (
            t("Publicar")
          )}
        </ModernButton>
      </div>
    </div>
  );
}
